/**
 * Path Game - Main Entry Point
 * Integrates all modules: auth, game, multiplayer, social, chat, history
 */

import './ui/styles/main.css';

// Game modules
import {
  GameEngine,
  GameRenderer,
  getAIMove,
  distance,
  type GameSettings,
  type PlayerConfig,
  type Move,
  type Point,
  type AIDifficulty,
} from './game';

// Auth modules
import { authService, authUI } from './auth';

// Social modules
import { presenceService, friendsService } from './social';

// Multiplayer modules
import { roomManager, type RoomInfo, type PlayerInfo, PartyKitClient } from './multiplayer';

// History modules
import { gameHistoryService } from './history';

// UI Components
import {
  Navigation,
  SettingsPanel,
  getSettings,
  RoomBrowser,
  WaitingRoom,
  Leaderboard,
  FriendsList,
  GameHistory,
  ReplayViewer,
  type Route,
} from './ui/components';

// Shared utilities
import { escapeHtml, showToast as showToastUtil } from './ui/utils';

// ============== Application State ==============

interface AppState {
  currentRoute: Route;
  navigation: Navigation | null;
  settingsPanel: SettingsPanel | null;
  
  // Game state
  engine: GameEngine | null;
  renderer: GameRenderer | null;
  gridSize: number;
  playerConfig: { 1: PlayerConfig; 2: PlayerConfig };
  computerThinking: boolean;
  replayMode: boolean;
  replayIndex: number;
  isOnlineGame: boolean;
  currentRoomInfo: RoomInfo | null;
  
  // Multiplayer state
  partyKitClient: PartyKitClient | null;
  myPlayerNumber: 1 | 2 | null;
  roomPlayers: PlayerInfo[];
  
  // UI Components
  roomBrowser: RoomBrowser | null;
  waitingRoom: WaitingRoom | null;
  leaderboard: Leaderboard | null;
  friendsList: FriendsList | null;
  gameHistory: GameHistory | null;
  replayViewer: ReplayViewer | null;
}

const state: AppState = {
  currentRoute: '/',
  navigation: null,
  settingsPanel: null,
  
  engine: null,
  renderer: null,
  gridSize: 7,
  playerConfig: {
    1: { type: 'human' },
    2: { type: 'computer', difficulty: 'medium' },
  },
  computerThinking: false,
  replayMode: false,
  replayIndex: 0,
  isOnlineGame: false,
  currentRoomInfo: null,
  
  // Multiplayer state
  partyKitClient: null,
  myPlayerNumber: null,
  roomPlayers: [],
  
  roomBrowser: null,
  waitingRoom: null,
  leaderboard: null,
  friendsList: null,
  gameHistory: null,
  replayViewer: null,
};

// ============== Toast Notifications ==============

function showToast(message: string, type: 'error' | 'success' | 'warning' | 'info' = 'info'): void {
  let container = document.querySelector('.toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'toast-container';
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = `toast ${type}`;
  toast.innerHTML = `
    <span class="toast-message">${message}</span>
    <button class="toast-close">&times;</button>
  `;

  container.appendChild(toast);

  const closeBtn = toast.querySelector('.toast-close');
  closeBtn?.addEventListener('click', () => toast.remove());

  setTimeout(() => toast.remove(), 5000);
}

// ============== DOM Elements ==============

function getElement<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

// ============== Route Handling ==============

function showRoute(route: Route): void {
  state.currentRoute = route;
  
  // Hide all route containers
  document.querySelectorAll('.route-container').forEach(el => {
    el.classList.remove('active');
  });
  
  // Cleanup previous route components
  cleanupRouteComponents();
  
  // Show target route
  const routeMap: Record<Route, string> = {
    '/': 'home-route',
    '/online': 'online-route',
    '/leaderboard': 'leaderboard-route',
    '/friends': 'friends-route',
    '/history': 'history-route',
    '/settings': 'settings-route',
  };
  
  const targetEl = document.getElementById(routeMap[route]);
  if (targetEl) {
    targetEl.classList.add('active');
    initializeRouteComponents(route);
  }
}

/** Helper to clean up a component */
function cleanupComponent<T extends { destroy(): void }>(
  component: T | null,
  setter: (val: null) => void
): void {
  if (component) {
    component.destroy();
    setter(null);
  }
}

function cleanupRouteComponents(): void {
  cleanupComponent(state.roomBrowser, () => state.roomBrowser = null);
  cleanupComponent(state.waitingRoom, () => state.waitingRoom = null);
  cleanupComponent(state.leaderboard, () => state.leaderboard = null);
  cleanupComponent(state.friendsList, () => state.friendsList = null);
  cleanupComponent(state.gameHistory, () => state.gameHistory = null);
}

function initializeRouteComponents(route: Route): void {
  switch (route) {
    case '/online':
      initOnlineRoute();
      break;
    case '/leaderboard':
      initLeaderboardRoute();
      break;
    case '/friends':
      initFriendsRoute();
      break;
    case '/history':
      initHistoryRoute();
      break;
    case '/settings':
      initSettingsRoute();
      break;
  }
}

// ============== Online Route ==============

function initOnlineRoute(): void {
  const container = document.getElementById('online-content');
  if (!container) return;
  
  // Lazy-initialize presence tracking when visiting multiplayer
  const user = authService.getCurrentUser();
  if (user) {
    presenceService.initialize();
  }
  
  container.innerHTML = '<div id="room-browser-container"></div>';
  const browserContainer = document.getElementById('room-browser-container');
  if (!browserContainer) return;
  
  state.roomBrowser = new RoomBrowser({
    onJoinRoom: (roomInfo) => handleJoinRoom(roomInfo),
    onCreateRoom: (roomInfo) => handleJoinRoom(roomInfo),
    onError: (error) => showToast(error, 'error'),
  });
  
  browserContainer.appendChild(state.roomBrowser.getElement());
}

async function handleJoinRoom(roomInfo: RoomInfo): Promise<void> {
  state.currentRoomInfo = roomInfo;
  state.isOnlineGame = true;
  
  const container = document.getElementById('online-content');
  if (!container) return;
  
  // Cleanup room browser
  if (state.roomBrowser) {
    state.roomBrowser.destroy();
    state.roomBrowser = null;
  }
  
  const user = authService.getCurrentUser();
  const currentUserId = user?.id || `guest-${Date.now()}`;
  
  // Create PartyKit client
  const { createPartyKitClient } = await import('./multiplayer');
  const host = import.meta.env.VITE_PARTYKIT_HOST || 'path-game.huang-jiaming.partykit.dev';
  const client = createPartyKitClient({
    host,
    roomId: roomInfo.code,
    userId: currentUserId,
    username: user?.displayName || user?.username || 'Guest',
    roomSettings: roomInfo.settings, // Pass room settings to PartyKit
  });
  
  // Store client in state for game synchronization
  state.partyKitClient = client;
  
  // Show loading state while connecting
  container.innerHTML = `
    <div class="waiting-room" style="text-align: center; padding: 60px 20px;">
      <div class="spinner" style="margin: 0 auto 20px;"></div>
      <p>Connecting to room...</p>
    </div>
  `;
  
  try {
    // Connect to the PartyKit server
    await client.connect();
    
    container.innerHTML = '<div id="waiting-room-container"></div>';
    const waitingContainer = document.getElementById('waiting-room-container');
    if (!waitingContainer) return;
    
    state.waitingRoom = new WaitingRoom({
      roomInfo,
      client,
      currentUserId,
      onGameStart: () => {
        // Start online game - pass the client for game sync
        startOnlineGame(roomInfo, client, currentUserId);
      },
      onLeave: () => {
        // Go back to room browser
        client.disconnect();
        state.partyKitClient = null;
        state.currentRoomInfo = null;
        state.isOnlineGame = false;
        state.myPlayerNumber = null;
        state.roomPlayers = [];
        initOnlineRoute();
      },
      onError: (error) => showToast(error, 'error'),
    });
    
    waitingContainer.appendChild(state.waitingRoom.getElement());
  } catch (error) {
    console.error('Failed to connect to room:', error);
    showToast('Failed to connect to room. Please try again.', 'error');
    state.partyKitClient = null;
    state.currentRoomInfo = null;
    state.isOnlineGame = false;
    state.roomPlayers = [];
    initOnlineRoute();
  }
}

async function rejoinRoom(roomCode: string, boardSize: number): Promise<void> {
  // Navigate to online view
  Navigation.navigateTo('/online');
  
  const container = document.getElementById('online-content');
  if (!container) return;
  
  // Show loading state
  container.innerHTML = `
    <div class="waiting-room" style="text-align: center; padding: 60px 20px;">
      <div class="spinner" style="margin: 0 auto 20px;"></div>
      <p>Rejoining room ${roomCode}...</p>
    </div>
  `;
  
  try {
    // Try to get room info first
    const roomInfo = await roomManager.getRoomInfo(roomCode);
    
    if (roomInfo && roomInfo.status !== 'finished') {
      // Room exists and is still active, join it
      await handleJoinRoom(roomInfo);
    } else {
      // Room doesn't exist or is finished, create a new one with same settings
      showToast('Previous room ended. Creating a new room...', 'info');
      const user = authService.getCurrentUser();
      if (user) {
        const newRoom = await roomManager.createRoom({
          isPublic: true,
          boardSize,
        }, user.id);
        await handleJoinRoom(newRoom);
      } else {
        showToast('Please sign in to create a room', 'error');
        initOnlineRoute();
      }
    }
  } catch (error) {
    console.error('Failed to rejoin room:', error);
    showToast('Failed to rejoin. Returning to lobby...', 'error');
    initOnlineRoute();
  }
}

function startOnlineGame(roomInfo: RoomInfo, client: PartyKitClient, currentUserId: string): void {
  // Switch to game view
  Navigation.navigateTo('/');
  
  // Start game with online settings
  state.gridSize = roomInfo.settings.boardSize;
  state.playerConfig = {
    1: { type: 'human' },
    2: { type: 'human' },
  };
  
  // Set up PartyKit callbacks for game synchronization
  client.setCallbacks({
    onGameStarted: (serverState) => {
      console.debug('[Multiplayer] Game started with initial state');
      if (state.engine) {
        state.engine.setState(serverState);
        // Clear any pending point when game starts
        state.renderer?.clearPendingPoint();
        updateDisplay();
        state.renderer?.draw();
      }
    },
    onGameState: (serverState, moveHistory) => {
      console.debug('[Multiplayer] Received game state update, moves:', moveHistory?.length || 0);
      if (state.engine) {
        // Update the local engine state from server
        state.engine.setState(serverState);
        // Sync move history from server
        if (moveHistory) {
          state.engine.setMoveHistory(moveHistory);
        }
        // Clear any pending point when state is synced from server
        state.renderer?.clearPendingPoint();
        updateDisplay();
        state.renderer?.draw();
      }
    },
    onMoveResult: (success, _move, error) => {
      console.debug('[Multiplayer] Move result:', { success, error });
      if (!success && error) {
        state.renderer?.showError(error);
      }
      // State update will come via onGameState
    },
    onGameEnded: (winner, serverState, moveHistory) => {
      console.debug('[Multiplayer] Game ended, winner:', winner, 'moves:', moveHistory?.length || 0);
      if (state.engine) {
        state.engine.setState(serverState);
        // Sync move history from server
        if (moveHistory) {
          state.engine.setMoveHistory(moveHistory);
        }
        state.renderer?.clearPendingPoint();
        updateDisplay();
        state.renderer?.draw();
        if (winner !== null) {
          handleGameOver(winner);
        }
      }
    },
    onPlayerUpdate: (players, _hostId, _status) => {
      // Store players for later use (e.g., when saving game)
      state.roomPlayers = players;
      
      // Find our player number
      const myPlayer = players.find(p => p.id === currentUserId);
      if (myPlayer) {
        state.myPlayerNumber = myPlayer.playerNumber;
        console.debug('[Multiplayer] My player number:', state.myPlayerNumber);
      }
    },
    onError: (error) => {
      showToast(error.message, 'error');
    },
    onDisconnect: () => {
      showToast('Disconnected from game server. Attempting to reconnect...', 'error');
    },
  });
  
  startGame();
}

// ============== Leaderboard Route ==============

function initLeaderboardRoute(): void {
  const container = document.getElementById('leaderboard-content');
  if (!container) return;
  
  state.leaderboard = new Leaderboard({
    container,
    onPlayerClick: (playerId) => {
      console.log('View player:', playerId);
      // Could open player stats modal
    },
  });
}

// ============== Friends Route ==============

function initFriendsRoute(): void {
  const container = document.getElementById('friends-content');
  if (!container) return;
  
  const user = authService.getCurrentUser();
  if (!user) {
    container.innerHTML = `
      <div class="text-center" style="padding: 60px 20px;">
        <h2>Sign in to see your friends</h2>
        <p class="text-muted" style="margin-bottom: 20px;">Connect with other players and see who's online.</p>
        <button class="settings-btn settings-btn-primary" id="friends-signin-btn">Sign In</button>
      </div>
    `;
    container.querySelector('#friends-signin-btn')?.addEventListener('click', () => {
      authUI.showLoginModal();
    });
    return;
  }
  
  container.innerHTML = '<div id="friends-list-container" class="page-container"></div>';
  const friendsContainer = document.getElementById('friends-list-container');
  if (!friendsContainer) return;
  
  state.friendsList = new FriendsList(friendsContainer);
  
  // Wire up game actions
  state.friendsList.onInviteToGame = async (friendId) => {
    try {
      const roomInfo = await roomManager.createRoom({
        isPublic: false,
        boardSize: getSettings().defaultBoardSize,
      });
      
      // In a real app, send invite notification to friend
      showToast(`Invite room created: ${roomInfo.code}`, 'success');
      Navigation.navigateTo('/online');
      setTimeout(() => handleJoinRoom(roomInfo), 100);
    } catch (error) {
      showToast('Failed to create invite room', 'error');
    }
  };
  
  state.friendsList.onJoinGame = (roomId) => {
    Navigation.navigateTo('/online');
    // Join the room
    roomManager.joinRoom(roomId).then(roomInfo => {
      if (roomInfo) {
        handleJoinRoom(roomInfo);
      }
    }).catch(() => {
      showToast('Failed to join game', 'error');
    });
  };
}

// ============== History Route ==============

function initHistoryRoute(): void {
  const container = document.getElementById('history-content');
  if (!container) return;
  
  const user = authService.getCurrentUser();
  if (!user) {
    container.innerHTML = `
      <div class="text-center" style="padding: 60px 20px;">
        <h2>Sign in to see your game history</h2>
        <p class="text-muted" style="margin-bottom: 20px;">Review past games and watch replays.</p>
        <button class="settings-btn settings-btn-primary" id="history-signin-btn">Sign In</button>
      </div>
    `;
    container.querySelector('#history-signin-btn')?.addEventListener('click', () => {
      authUI.showLoginModal();
    });
    return;
  }
  
  state.gameHistory = new GameHistory({
    container,
    onGameSelect: (gameId) => {
      // Load full game and open replay viewer
      loadGameAndShowReplay(gameId);
    },
  });
}

async function loadGameAndShowReplay(gameId: string): Promise<void> {
  console.log('[Replay] Loading game:', gameId);
  try {
    const game = await gameHistoryService.getGame(gameId);
    console.log('[Replay] Loaded game:', game);
    if (game) {
      openReplayViewer(game);
    } else {
      console.error('[Replay] Game not found or failed to load');
      showToast('Failed to load game', 'error');
    }
  } catch (error) {
    console.error('[Replay] Error loading game:', error);
    showToast('Failed to load game replay', 'error');
  }
}

function openReplayViewer(game: unknown): void {
  console.log('[Replay] Opening replay viewer with game:', game);
  
  const overlay = document.createElement('div');
  overlay.id = 'replay-viewer-overlay';
  // Ensure the overlay is visible and takes full screen
  overlay.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999;';
  document.body.appendChild(overlay);
  
  state.replayViewer = new ReplayViewer({
    container: overlay,
    onClose: () => {
      console.log('[Replay] Closing replay viewer');
      if (state.replayViewer) {
        state.replayViewer.destroy();
        state.replayViewer = null;
      }
      overlay.remove();
    },
  });
  
  // Open with the game data
  const gameData = game as Parameters<typeof state.replayViewer.openWithData>[0];
  console.log('[Replay] Move history length:', gameData?.moveHistory?.length);
  state.replayViewer.openWithData(gameData);
}

// ============== Settings Route ==============

function initSettingsRoute(): void {
  const container = document.getElementById('settings-content');
  if (!container) return;
  
  state.settingsPanel = new SettingsPanel(container, {
    onClose: () => Navigation.navigateTo('/'),
    onSettingsChange: (settings) => {
      console.log('Settings changed:', settings);
    },
  }, false); // inline mode
}

// ============== Settings Modal ==============

function showSettingsModal(): void {
  const overlay = document.createElement('div');
  overlay.id = 'settings-modal-overlay';
  document.body.appendChild(overlay);
  
  const panel = new SettingsPanel(overlay, {
    onClose: () => {
      panel.destroy();
      overlay.remove();
    },
  }, true); // modal mode
}

// ============== Game Setup ==============

function setupGameEventListeners(): void {
  const gameSetup = getElement('gameSetup');
  const startGameBtn = getElement('startGameBtn');
  const resetBtn = getElement('resetBtn');
  
  if (!gameSetup || !startGameBtn || !resetBtn) return;
  
  // Board size buttons
  document.querySelectorAll('.size-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.size-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      state.gridSize = parseInt((btn as HTMLButtonElement).dataset.size || '7');
    });
  });
  
  // Player type buttons
  document.querySelectorAll('.type-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const playerNum = (btn as HTMLButtonElement).dataset.player as '1' | '2';
      const type = (btn as HTMLButtonElement).dataset.type as 'human' | 'computer';
      
      document
        .querySelectorAll(`.type-btn[data-player="${playerNum}"]`)
        .forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      
      state.playerConfig[playerNum].type = type;
      
      // Show/hide difficulty selector
      const difficultySelect = document.getElementById(`difficulty-${playerNum}`);
      if (difficultySelect) {
        difficultySelect.style.display = type === 'computer' ? 'flex' : 'none';
      }
      
      if (type === 'computer' && !state.playerConfig[playerNum].difficulty) {
        state.playerConfig[playerNum].difficulty = 'medium';
      }
    });
  });
  
  // Difficulty selectors
  document.querySelectorAll('.diff-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const playerNum = (btn as HTMLButtonElement).dataset.player as '1' | '2';
      const difficulty = (btn as HTMLButtonElement).dataset.difficulty as AIDifficulty;
      
      document
        .querySelectorAll(`.diff-btn[data-player="${playerNum}"]`)
        .forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      
      state.playerConfig[playerNum].difficulty = difficulty;
    });
  });
  
  // Start game button
  startGameBtn.addEventListener('click', startGame);
  
  // Reset button
  resetBtn.addEventListener('click', () => {
    showGameSetup();
  });
  
  // Replay buttons
  document.getElementById('replayFirst')?.addEventListener('click', replayFirst);
  document.getElementById('replayPrev')?.addEventListener('click', replayPrev);
  document.getElementById('replayNext')?.addEventListener('click', replayNext);
  document.getElementById('replayLast')?.addEventListener('click', replayLast);
}

function showGameSetup(): void {
  const gameSetup = getElement('gameSetup');
  const gameContainer = getElement('gameContainer');
  
  if (gameSetup) gameSetup.style.display = 'flex';
  if (gameContainer) gameContainer.style.display = 'none';
  
  const overlay = document.querySelector('.winner-overlay');
  if (overlay) overlay.remove();
  
  state.replayMode = false;
  state.isOnlineGame = false;
  state.currentRoomInfo = null;
  state.roomPlayers = [];
}

function startGame(): void {
  const gameSetup = getElement('gameSetup');
  const gameContainer = getElement('gameContainer');
  const canvas = getElement<HTMLCanvasElement>('gameCanvas');
  
  if (!gameSetup || !gameContainer || !canvas) return;
  
  gameSetup.style.display = 'none';
  gameContainer.style.display = 'flex';
  
  // Clean up old renderer if it exists (removes event listeners)
  if (state.renderer) {
    state.renderer.destroy();
    state.renderer = null;
  }
  
  // Create game settings
  const settings: GameSettings = {
    gridSize: state.gridSize,
    player1: { ...state.playerConfig[1] },
    player2: { ...state.playerConfig[2] },
  };
  
  // Create engine
  state.engine = new GameEngine(settings);
  
  // Create renderer
  state.renderer = new GameRenderer(canvas, state.engine, state.playerConfig);
  state.renderer.setMoveCallback(handleMoveAttempt);
  
  // Setup event listeners
  state.engine.on('stateChange', () => {
    updateDisplay();
    state.renderer?.draw();
  });
  
  state.engine.on('gameOver', (event) => {
    updateDisplay();
    state.renderer?.draw();
    handleGameOver(event.detail.winner);
  });
  
  state.engine.on('invalidMove', (event) => {
    state.renderer?.showError(event.detail.reason);
  });
  
  // Update player badges
  updatePlayerBadges();
  
  // Hide replay controls
  const replayControls = getElement('replayControls');
  const replayInfo = getElement('replayInfo');
  if (replayControls) replayControls.style.display = 'none';
  if (replayInfo) replayInfo.style.display = 'none';
  state.replayMode = false;
  
  // Add in-game chat for online games
  if (state.isOnlineGame && state.partyKitClient) {
    addInGameChat(gameContainer);
  }
  
  // Initial render
  updateDisplay();
  state.renderer.draw();
  
  // Trigger computer move if player 1 is computer
  if (state.playerConfig[1].type === 'computer') {
    setTimeout(() => triggerComputerMove(), 500);
  }
}

function addInGameChat(gameContainer: HTMLElement): void {
  // Remove existing chat panel if any
  const existingChat = document.getElementById('in-game-chat');
  if (existingChat) existingChat.remove();
  
  // Create chat panel (styles are in chat.css)
  const chatPanel = document.createElement('div');
  chatPanel.id = 'in-game-chat';
  chatPanel.className = 'in-game-chat';
  chatPanel.innerHTML = `
    <div class="chat-header">
      <h4>Chat</h4>
      <button class="chat-toggle" id="chat-toggle">−</button>
    </div>
    <div class="chat-body" id="chat-body">
      <div class="chat-messages" id="game-chat-messages">
        <div class="chat-empty">No messages yet</div>
      </div>
      <div class="chat-input-row">
        <input type="text" id="game-chat-input" placeholder="Type a message..." maxlength="200">
        <button id="game-chat-send">Send</button>
      </div>
    </div>
  `;
  
  gameContainer.appendChild(chatPanel);
  
  // Setup chat functionality
  const toggleBtn = document.getElementById('chat-toggle');
  const chatBody = document.getElementById('chat-body');
  const chatInput = document.getElementById('game-chat-input') as HTMLInputElement;
  const sendBtn = document.getElementById('game-chat-send');
  const messagesContainer = document.getElementById('game-chat-messages');
  
  // Toggle chat
  toggleBtn?.addEventListener('click', () => {
    chatBody?.classList.toggle('collapsed');
    if (toggleBtn) {
      toggleBtn.textContent = chatBody?.classList.contains('collapsed') ? '+' : '−';
    }
  });
  
  // Send message
  const sendMessage = () => {
    const content = chatInput?.value.trim();
    if (content && state.partyKitClient) {
      state.partyKitClient.sendChatMessage(content);
      chatInput.value = '';
    }
  };
  
  sendBtn?.addEventListener('click', sendMessage);
  chatInput?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') sendMessage();
  });
  
  // Handle incoming chat messages - track seen IDs to prevent duplicates
  const seenMessageIds = new Set<string>();
  
  if (state.partyKitClient) {
    state.partyKitClient.onChatMessage((message) => {
      if (!messagesContainer) return;
      
      // Deduplicate messages by ID
      if (seenMessageIds.has(message.id)) return;
      seenMessageIds.add(message.id);
      
      // Remove "No messages" placeholder
      const empty = messagesContainer.querySelector('.chat-empty');
      if (empty) empty.remove();
      
      const user = authService.getCurrentUser();
      const isOwn = message.senderId === user?.id;
      
      const msgEl = document.createElement('div');
      msgEl.className = `chat-message ${isOwn ? 'own' : ''}`;
      msgEl.innerHTML = `
        <span class="sender">${escapeHtml(message.senderName)}:</span>
        <span class="content">${escapeHtml(message.content)}</span>
      `;
      messagesContainer.appendChild(msgEl);
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    });
  }
}

// escapeHtml is imported from './ui/utils'

function updatePlayerBadges(): void {
  const baseNames = { 1: 'Player 1', 2: 'Player 2' };
  for (let p = 1; p <= 2; p++) {
    const header = document.querySelector(`#p${p}Info h3`) as HTMLHeadingElement;
    if (!header) continue;
    
    const config = state.playerConfig[p as 1 | 2];
    if (config.type === 'computer') {
      const diffLabel = config.difficulty ? ` (${config.difficulty.charAt(0).toUpperCase() + config.difficulty.slice(1)})` : '';
      header.innerHTML = `${baseNames[p as 1 | 2]}<span class="computer-badge">AI${diffLabel}</span>`;
    } else {
      header.innerHTML = baseNames[p as 1 | 2];
    }
  }
}

// ============== Game Logic ==============

function handleMoveAttempt(from: Point, to: Point, isFirstMove: boolean): void {
  if (!state.engine || !state.renderer) return;
  if (state.computerThinking || state.replayMode) return;
  
  const gameState = state.engine.getState();
  
  // In online mode, check if it's our turn and send move to server
  if (state.isOnlineGame && state.partyKitClient) {
    // Check if it's our turn
    if (state.myPlayerNumber !== gameState.currentPlayer) {
      state.renderer.showError("It's not your turn!");
      return;
    }
    
    // Send move to server - server will validate and broadcast
    const move: Move = { from, to, isFirstMove };
    console.debug('[Multiplayer] Sending move to server:', move);
    state.partyKitClient.sendMove(move);
    state.renderer.clearPendingPoint();
    return;
  }
  
  // Local game logic
  if (state.playerConfig[gameState.currentPlayer].type === 'computer') return;
  
  const move: Move = { from, to, isFirstMove };
  const success = state.engine.makeMove(move);
  
  if (success) {
    state.renderer.clearPendingPoint();
    updateDisplay();
    state.renderer.draw();
    
    // Check if next player is computer
    const newState = state.engine.getState();
    if (!newState.gameOver && state.playerConfig[newState.currentPlayer].type === 'computer') {
      setTimeout(() => triggerComputerMove(), 500);
    }
  }
}

function triggerComputerMove(): void {
  if (!state.engine || !state.renderer) return;
  
  const gameState = state.engine.getState();
  if (gameState.gameOver || state.computerThinking || state.replayMode) return;
  if (state.playerConfig[gameState.currentPlayer].type !== 'computer') return;
  
  state.computerThinking = true;
  updateDisplay();
  
  setTimeout(() => {
    if (!state.engine) return;
    
    const currentState = state.engine.getState();
    const difficulty = state.playerConfig[currentState.currentPlayer].difficulty || 'medium';
    const move = getAIMove(currentState, currentState.currentPlayer, difficulty as AIDifficulty);
    
    state.computerThinking = false;
    
    if (move) {
      state.engine.makeMove(move);
      updateDisplay();
      state.renderer?.draw();
      
      // Check if next player is also computer
      const newState = state.engine.getState();
      if (!newState.gameOver && state.playerConfig[newState.currentPlayer].type === 'computer') {
        setTimeout(() => triggerComputerMove(), 500);
      }
    }
  }, 300);
}

// ============== Game Over Handling ==============

function handleGameOver(winner: 1 | 2): void {
  showWinner(winner);
  // Note: Online games are saved by the PartyKit server, not the client
}


function showWinner(playerNum: 1 | 2): void {
  // Remove any existing overlays first
  document.querySelectorAll('.winner-overlay').forEach(el => el.remove());
  
  const isOnline = state.isOnlineGame;
  const user = authService.getCurrentUser();
  const hasAI = state.playerConfig[1].type === 'computer' || state.playerConfig[2].type === 'computer';
  const showSaveBtn = user && !isOnline && hasAI;
  
  const playerLabel = isOnline && state.myPlayerNumber 
    ? (playerNum === state.myPlayerNumber ? 'You Win!' : 'You Lose!')
    : `Player ${playerNum} Wins!`;
  
  const overlay = document.createElement('div');
  overlay.className = 'winner-overlay';
  overlay.innerHTML = `
    <div class="winner-modal p${playerNum}">
      <h2>${playerLabel}</h2>
      <p style="margin-bottom: 20px; color: #aaa;">Made the last valid move</p>
      <div style="display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;">
        <button id="reviewGameBtn" style="padding: 12px 24px; background: #3498db; border: none; border-radius: 8px; color: white; font-size: 15px; cursor: pointer;">Review Game</button>
        <button id="playAgainBtn" style="padding: 12px 24px; background: #9b59b6; border: none; border-radius: 8px; color: white; font-size: 15px; cursor: pointer;">${isOnline ? 'Back to Lobby' : 'Play Again'}</button>
        <button id="saveGameBtn" style="display: ${showSaveBtn ? 'inline-block' : 'none'}; padding: 12px 24px; background: #27ae60; border: none; border-radius: 8px; color: white; font-size: 15px; cursor: pointer;">Save Game</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  
  const reviewBtn = overlay.querySelector('#reviewGameBtn');
  const playAgainBtn = overlay.querySelector('#playAgainBtn');
  const saveBtn = overlay.querySelector('#saveGameBtn') as HTMLButtonElement | null;
  
  // Save room info for "Play Again" before any potential cleanup
  const roomInfoForPlayAgain = state.currentRoomInfo;
  const gridSizeForReplay = state.gridSize;
  
  reviewBtn?.addEventListener('click', () => {
    overlay.remove();
    
    // Get move history before any cleanup
    const moveHistory = state.engine?.getMoveHistory() || [];
    const boardSize = state.gridSize;
    const gameWinner = playerNum;
    
    // Disconnect PartyKit
    if (state.isOnlineGame && state.partyKitClient) {
      state.partyKitClient.disconnect();
      state.partyKitClient = null;
    }
    // Remove chat panel
    const chatPanel = document.getElementById('in-game-chat');
    if (chatPanel) chatPanel.remove();
    
    // Get player info for the replay viewer
    const user = authService.getCurrentUser();
    const players = state.roomPlayers;
    const player1 = players.find(p => p.playerNumber === 1);
    const player2 = players.find(p => p.playerNumber === 2);
    
    // Create game data for replay viewer
    const gameData = {
      id: `replay-${Date.now()}`,
      player1Id: player1?.id || user?.id || null,
      player2Id: player2?.id || null,
      winnerId: gameWinner === 1 ? (player1?.id || user?.id || null) : (player2?.id || null),
      boardSize,
      moveHistory,
      startedAt: new Date().toISOString(),
      endedAt: new Date().toISOString(),
      status: 'completed' as const,
      isRanked: isOnline,
      gameType: (isOnline ? 'online' : state.playerConfig[2].type === 'computer' ? 'vs_ai' : 'local') as 'online' | 'local' | 'vs_ai',
      player1: player1 ? { id: player1.id, username: player1.name, displayName: null, avatarUrl: null } : 
               user ? { id: user.id, username: user.username || 'Player 1', displayName: user.displayName, avatarUrl: user.avatarUrl } : 
               { id: 'p1', username: 'Player 1', displayName: null, avatarUrl: null },
      player2: player2 ? { id: player2.id, username: player2.name, displayName: null, avatarUrl: null } :
               { id: 'p2', username: isOnline ? 'Player 2' : (state.playerConfig[2].type === 'computer' ? 'Computer' : 'Player 2'), displayName: null, avatarUrl: null },
      winner: gameWinner === 1 ? 
        (player1 ? { id: player1.id, username: player1.name, displayName: null, avatarUrl: null } : 
         user ? { id: user.id, username: user.username || 'Player 1', displayName: user.displayName, avatarUrl: user.avatarUrl } :
         { id: 'p1', username: 'Player 1', displayName: null, avatarUrl: null }) :
        (player2 ? { id: player2.id, username: player2.name, displayName: null, avatarUrl: null } :
         { id: 'p2', username: 'Player 2', displayName: null, avatarUrl: null }),
    };
    
    // Open replay viewer modal
    openReplayViewer(gameData);
  });
  
  playAgainBtn?.addEventListener('click', () => {
    overlay.remove();
    if (isOnline) {
      // Save room code before cleanup
      const roomCode = roomInfoForPlayAgain?.code;
      cleanupOnlineGame();
      
      if (roomCode) {
        // Try to rejoin the same room
        showToast('Rejoining room...', 'info');
        rejoinRoom(roomCode, gridSizeForReplay);
      } else {
        Navigation.navigateTo('/online');
      }
    } else {
      showGameSetup();
    }
  });
  
  // Save game button for AI games
  if (saveBtn && showSaveBtn && state.engine) {
    const moveHistory = state.engine.getMoveHistory();
    saveBtn.addEventListener('click', async () => {
      if (!user || moveHistory.length < 2) return;
      
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving...';
      
      const gameId = await gameHistoryService.saveLocalGame({
        player2Id: null,
        winnerId: null,
        gameType: 'vs_ai',
        boardSize: state.gridSize,
        moveHistory,
        isRanked: false,
      });
      
      if (gameId) {
        saveBtn.textContent = 'Saved!';
        saveBtn.style.background = '#666';
        showToast('Game saved to history!', 'success');
      } else {
        saveBtn.textContent = 'Save Failed';
        saveBtn.style.background = '#c0392b';
        showToast('Failed to save game', 'error');
      }
    });
  }
}

function cleanupOnlineGame(): void {
  // Remove chat panel
  const chatPanel = document.getElementById('in-game-chat');
  if (chatPanel) chatPanel.remove();
  
  // Disconnect PartyKit
  if (state.partyKitClient) {
    state.partyKitClient.disconnect();
    state.partyKitClient = null;
  }
  
  // Clean up game state
  state.isOnlineGame = false;
  state.myPlayerNumber = null;
  state.currentRoomInfo = null;
  state.roomPlayers = [];
  state.engine = null;
  // Destroy renderer to remove event listeners before nulling
  if (state.renderer) {
    state.renderer.destroy();
    state.renderer = null;
  }
  state.replayMode = false;
  
  // Hide game container, show setup
  const gameContainer = getElement('gameContainer');
  const gameSetup = getElement('gameSetup');
  if (gameContainer) gameContainer.style.display = 'none';
  if (gameSetup) gameSetup.style.display = 'flex';
  
  // Remove any winner overlays
  document.querySelectorAll('.winner-overlay').forEach(el => el.remove());
}

function resetGame(): void {
  if (!state.engine || !state.renderer) return;
  
  state.engine.resetGame();
  state.renderer.clearPendingPoint();
  state.computerThinking = false;
  state.replayMode = false;
  
  // Hide replay controls
  const replayControls = getElement('replayControls');
  const replayInfo = getElement('replayInfo');
  if (replayControls) replayControls.style.display = 'none';
  if (replayInfo) replayInfo.style.display = 'none';
  
  updateDisplay();
  state.renderer.draw();
  
  // Remove any overlay
  const overlay = document.querySelector('.winner-overlay');
  if (overlay) overlay.remove();
  
  // Trigger computer move if player 1 is computer
  if (state.playerConfig[1].type === 'computer') {
    setTimeout(() => triggerComputerMove(), 500);
  }
}

// ============== Display Updates ==============

function updateDisplay(): void {
  if (!state.engine) return;
  
  const gameState = state.engine.getState();
  const p1 = gameState.players[1];
  const p2 = gameState.players[2];
  
  // Update player positions and stats
  const p1Pos = getElement('p1Pos');
  const p1Dist = getElement('p1Dist');
  const p1Min = getElement('p1Min');
  const p2Pos = getElement('p2Pos');
  const p2Dist = getElement('p2Dist');
  const p2Min = getElement('p2Min');
  const statusEl = getElement('status');
  const p1Info = getElement('p1Info');
  const p2Info = getElement('p2Info');
  
  if (p1Pos) p1Pos.textContent = p1.pos ? `${String.fromCharCode(65 + p1.pos.x)}${p1.pos.y + 1}` : '—';
  if (p1Dist) p1Dist.textContent = p1.lastDist > 0 ? p1.lastDist.toFixed(2) : '—';
  if (p1Min) p1Min.textContent = p1.lastDist > 0 ? `> ${p1.lastDist.toFixed(2)}` : 'any';
  
  if (p2Pos) p2Pos.textContent = p2.pos ? `${String.fromCharCode(65 + p2.pos.x)}${p2.pos.y + 1}` : '—';
  if (p2Dist) p2Dist.textContent = p2.lastDist > 0 ? p2.lastDist.toFixed(2) : '—';
  if (p2Min) p2Min.textContent = p2.lastDist > 0 ? `> ${p2.lastDist.toFixed(2)}` : 'any';
  
  // Update active player highlight
  p1Info?.classList.toggle('active', gameState.currentPlayer === 1);
  p2Info?.classList.toggle('active', gameState.currentPlayer === 2);
  
  // Update status
  if (!statusEl) return;
  
  if (state.replayMode) {
    return; // Status handled by replay functions
  } else if (gameState.gameOver) {
    statusEl.textContent = 'Game Over!';
  } else if (state.computerThinking) {
    const playerLabel = gameState.currentPlayer === 1 ? 'Red' : 'Blue';
    statusEl.innerHTML = `<div class="computer-thinking">${playerLabel} AI thinking<div class="thinking-dots"><span></span><span></span><span></span></div></div>`;
  } else if (state.playerConfig[gameState.currentPlayer].type === 'computer') {
    statusEl.textContent = `Player ${gameState.currentPlayer}'s turn (AI)`;
  } else if (!gameState.players[gameState.currentPlayer].pos) {
    statusEl.textContent = `Player ${gameState.currentPlayer}'s turn - Click first point`;
  } else {
    statusEl.textContent = `Player ${gameState.currentPlayer}'s turn - Click to move`;
  }
}

// ============== Replay Functionality ==============

function startReplay(): void {
  if (!state.engine) return;
  
  const moveHistory = state.engine.getMoveHistory();
  if (moveHistory.length === 0) return;
  
  state.replayMode = true;
  // Start at final state (show all moves)
  state.replayIndex = moveHistory.length;
  
  // Show replay controls
  const replayControls = getElement('replayControls');
  const replayInfo = getElement('replayInfo');
  if (replayControls) replayControls.style.display = 'flex';
  if (replayInfo) replayInfo.style.display = 'block';
  
  renderReplayState();
}

function renderReplayState(): void {
  if (!state.engine || !state.renderer) return;
  
  const moveHistory = state.engine.getMoveHistory();
  const gameState = state.engine.getState();
  
  // Rebuild state up to replayIndex
  const tempPlayers = {
    1: { pos: null as Point | null, path: [] as Point[], lastDist: 0 },
    2: { pos: null as Point | null, path: [] as Point[], lastDist: 0 },
  };
  const tempPoints: Array<{ x: number; y: number; player: 1 | 2 }> = [];
  
  for (let i = 0; i < state.replayIndex; i++) {
    const move = moveHistory[i];
    const player = tempPlayers[move.player];
    
    if (move.isFirstMove) {
      tempPoints.push({ x: move.from.x, y: move.from.y, player: move.player });
      tempPoints.push({ x: move.to.x, y: move.to.y, player: move.player });
      player.path.push({ ...move.from });
      player.path.push({ ...move.to });
      player.pos = { ...move.to };
      player.lastDist = distance(move.from, move.to);
    } else {
      tempPoints.push({ x: move.to.x, y: move.to.y, player: move.player });
      player.path.push({ ...move.to });
      player.pos = { ...move.to };
      player.lastDist = distance(move.from, move.to);
    }
  }
  
  // Get highlight move (last move shown)
  const highlightMove = state.replayIndex > 0 ? moveHistory[state.replayIndex - 1] : null;
  
  // Draw the replay state
  state.renderer.drawReplayState(tempPlayers, tempPoints, highlightMove, gameState.gridSize);
  updateReplayInfo();
}

function updateReplayInfo(): void {
  if (!state.engine) return;
  
  const moveHistory = state.engine.getMoveHistory();
  const statusEl = getElement('status');
  const replayMoveNum = getElement('replayMoveNum');
  const replayTotalMoves = getElement('replayTotalMoves');
  
  if (replayMoveNum) replayMoveNum.textContent = state.replayIndex.toString();
  if (replayTotalMoves) replayTotalMoves.textContent = moveHistory.length.toString();
  
  // Update button states
  const firstBtn = getElement<HTMLButtonElement>('replayFirst');
  const prevBtn = getElement<HTMLButtonElement>('replayPrev');
  const nextBtn = getElement<HTMLButtonElement>('replayNext');
  const lastBtn = getElement<HTMLButtonElement>('replayLast');
  
  if (firstBtn) firstBtn.disabled = state.replayIndex === 0;
  if (prevBtn) prevBtn.disabled = state.replayIndex === 0;
  if (nextBtn) nextBtn.disabled = state.replayIndex >= moveHistory.length;
  if (lastBtn) lastBtn.disabled = state.replayIndex >= moveHistory.length;
  
  // Update status
  if (statusEl) {
    if (state.replayIndex === 0) {
      statusEl.textContent = 'Replay: Game start';
    } else if (state.replayIndex >= moveHistory.length) {
      statusEl.textContent = 'Replay: Game end';
    } else {
      const lastMove = moveHistory[state.replayIndex - 1];
      statusEl.textContent = `Replay: Player ${lastMove.player} moved`;
    }
  }
}

function replayFirst(): void {
  state.replayIndex = 0;
  renderReplayState();
}

function replayPrev(): void {
  if (state.replayIndex > 0) {
    state.replayIndex--;
    renderReplayState();
  }
}

function replayNext(): void {
  if (!state.engine) return;
  const moveHistory = state.engine.getMoveHistory();
  if (state.replayIndex < moveHistory.length) {
    state.replayIndex++;
    renderReplayState();
  }
}

function replayLast(): void {
  if (!state.engine) return;
  const moveHistory = state.engine.getMoveHistory();
  state.replayIndex = moveHistory.length;
  renderReplayState();
}

// ============== Auth State Handling ==============

function handleAuthStateChange(): void {
  const user = authService.getCurrentUser();
  
  if (!user) {
    // Stop presence tracking when user signs out
    presenceService.cleanup();
  }
  // Note: Presence tracking is now initialized lazily when visiting /online or /friends
  
  // Re-render navigation to update auth-dependent items
  if (state.navigation) {
    state.navigation.render();
  }
  
  // Refresh current route if it requires auth
  if (state.currentRoute !== '/') {
    initializeRouteComponents(state.currentRoute);
  }
}

// ============== Initialize Application ==============

async function init(): Promise<void> {
  console.log('🎮 Path Game initializing...');
  
  // Check for Supabase config
  if (!authService.isAuthAvailable()) {
    console.warn('⚠️ Supabase not configured - online features disabled');
  }
  
  // Subscribe to auth changes
  authService.onAuthStateChange(() => {
    handleAuthStateChange();
  });
  
  // Create navigation
  const navContainer = document.getElementById('navigation');
  if (navContainer) {
    state.navigation = new Navigation(navContainer, {
      onRouteChange: (route) => showRoute(route),
      onSettingsClick: () => showSettingsModal(),
    });
  }
  
  // Setup game event listeners
  setupGameEventListeners();
  
  // Apply saved settings
  const settings = getSettings();
  state.gridSize = settings.defaultBoardSize;
  
  // Set initial board size button
  document.querySelectorAll('.size-btn').forEach(btn => {
    const size = parseInt((btn as HTMLButtonElement).dataset.size || '7');
    btn.classList.toggle('active', size === state.gridSize);
  });
  
  console.log('✅ Path Game initialized');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
